import {
  EmailAlreadyRegisteredAlert,
  EmailAlreadyRegisteredNoPasswordAlert,
  IdentifyAndVerifyGenericErrorAlert,
  NotEnoughInfoAlert,
} from '@/components';
import { USER_REGISTRATION_JOURNEY_KEY_OPTIONS } from '@/context/UserRegistrationContext';
import { userRegistrationJourneyConfig } from '@/utils/identifyAndVerify';

type RegistrationStatusAlertProps = {
  accountIdentificationData?: {
    blackholeEmailAccountUser?: {
      hasSetPassword: boolean;
      hasDateOfBirth: boolean;
      hasMobileNumber: boolean;
    };
  };
  userRegistrationStatus?: {
    email?: string | null;
  };
  journeyKey: USER_REGISTRATION_JOURNEY_KEY_OPTIONS;
  notEnoughInfoToProceed: boolean;
  genericError?: boolean;
};

/**
 * Component that determines which alert to show based on user's registration status.
 * Shows either:
 * 1. Email registration alert (if user has registered email)
 *    - EmailAlreadyRegisteredAlert if they have set a password
 *    - EmailAlreadyRegisteredNoPasswordAlert if they haven't set a password
 * 2. Not enough info alert (if user hasn't registered email and lacks required info)
 * Never shows both alerts at the same time
 */
const RegistrationStatusAlert = ({
  accountIdentificationData,
  userRegistrationStatus,
  journeyKey,
  notEnoughInfoToProceed,
  genericError,
}: RegistrationStatusAlertProps) => {
  // If there is a generic error, show the generic error alert
  if (genericError) {
    return <IdentifyAndVerifyGenericErrorAlert />;
  }

  const blackholeEmailAccountUser =
    accountIdentificationData?.blackholeEmailAccountUser;
  const email = userRegistrationStatus?.email;

  // Check if we should show the not enough info alert based on journey config
  const canShowNotEnoughInfoAlert =
    userRegistrationJourneyConfig[journeyKey].showDetailedNotEnoughInfoAlert;

  // Check if user has registered email and we should block for registered emails
  const hasRegisteredEmail =
    accountIdentificationData &&
    userRegistrationJourneyConfig[journeyKey].blockForRegisteredEmails &&
    email;

  const hasAlreadySetPassword = blackholeEmailAccountUser?.hasSetPassword;

  // If user has registered email, show appropriate alert based on password status
  if (hasRegisteredEmail && email) {
    return hasAlreadySetPassword ? (
      <EmailAlreadyRegisteredAlert email={email} />
    ) : (
      <EmailAlreadyRegisteredNoPasswordAlert email={email} />
    );
  }

  // If user hasn't registered email and lacks required info, show not enough info alert
  if (canShowNotEnoughInfoAlert && notEnoughInfoToProceed) {
    return <NotEnoughInfoAlert />;
  }

  return null;
};

export default RegistrationStatusAlert;
